// Import React
import React from "react";

// Import hooks
import useAiQueueManager from "pages/ai_image_generation/hooks/useAiQueueManager";
import usePodDesignUpscaleFlow from "./hooks/usePodDesignUpscaleFlow";

// Import components
import CreateButton from "global/components/CreateButton";
import RadialProgress from "global/components/RadialProgress";

// Import CSS
import "../ai_pod_design/PodDesignAiPage.css";
import "./PodDesignUpscalePage.css";



// Export the main page component
export default function PodDesignUpscalePage({
    sourceImage,
    sourceFileType,
    sourceWidth,
    sourceHeight,
    setSubmittedUpscaledImage,
    setShowPodDesignUpscalePage,
}) {

    // Get queue helper functions for the progress ring
    const { getQueueItemProgressValue, getQueueItemProgressColor } = useAiQueueManager({});

    // Get the page state and handlers from the custom hook
    const {
        selectedUpscaler,
        setSelectedUpscaler,
        upscaleOptions,
        selectedUpscalerInfo,
        upscaleQueueItem,
        isUpscaling,
        currentImageUrl,
        currentWidth,
        currentHeight,
        upscaleLimitReached,
        canReset,
        canUpscale,
        canSubmit,
        handleResetUpscale,
        handleUpscaleImage,
        handleSubmitUpscaledImage,
    } = usePodDesignUpscaleFlow({
        sourceImage,
        sourceFileType,
        sourceWidth,
        sourceHeight,
        setSubmittedUpscaledImage,
        setShowPodDesignUpscalePage,
    });

    // Show the queue progress state while an upscale job is running
    const showUpscalingState = isUpscaling;

    return (

        <div className="podDesignUpscalePage">

            {/* This is the main card */}
            <div className="podDesignUpscalePage__card">

                {/* This is the page body */}
                <div className="podDesignUpscalePage__body">

                    {/* This is the two-column layout */}
                    <div className="podDesignUpscalePage__layout">

                        {/* This is the settings panel */}
                        <div className="podDesignUpscalePage__settingsPanel">

                            {/* This is the helper block */}
                            <div className="podDesignUpscalePage__helperBlock">

                                <label className="podDesignUpscalePage__label">
                                    Upscale Options
                                </label>

                                <p className="podDesignUpscalePage__helperText">
                                    Choose an upscaler, run it, review the output, then submit it to replace the current design image.
                                </p>

                            </div>

                            {/* This is the upscaler button group */}
                            <div className="podDesignUpscalePage__buttonGroup">

                                {(upscaleOptions || []).map((option) => {

                                    // Check whether this option is the selected one
                                    const isSelected = selectedUpscaler === option.value;

                                    return (

                                        <button
                                            key={option.value}
                                            type="button"
                                            className={`podDesignUpscalePage__optionButton ${isSelected ? "podDesignUpscalePage__optionButton--selected" : ""}`}
                                            onClick={() => setSelectedUpscaler(option.value)}
                                            disabled={isUpscaling}
                                        >
                                            {option.label}
                                        </button>

                                    );

                                })}

                            </div>

                            {/* This is the selected option info box */}
                            <div className="podDesignUpscalePage__infoBox">

                                <div className="podDesignUpscalePage__infoTitle">
                                    {selectedUpscalerInfo?.label || "Upscaler"}
                                </div>

                                <p className="podDesignUpscalePage__infoText">
                                    {selectedUpscalerInfo?.description || "Upscale the current design image."}
                                </p>

                            </div>

                        </div>

                        {/* This is the main preview panel */}
                        <div className="podDesignUpscalePage__mainPanel">

                            {/* This is the preview label */}
                            <label className="podDesignAiPromptForm__label podDesignAiPromptForm__previewLabel">
                                Preview
                            </label>

                            {/* This is the fixed preview panel */}
                            <div className="podDesignAiPromptForm__staticPreviewPanel">

                                {/* This is the fixed preview body */}
                                <div className="podDesignAiPromptForm__staticPreviewBody transparency-bg">

                                    {/* Show the current working image when an upscale is not running */}
                                    {!showUpscalingState && (

                                        <div className="podDesignAiPromptForm__staticPreviewMedia podDesignUpscalePage__previewMedia transparency-bg">

                                            <img
                                                className="podDesignAiPromptForm__staticPreviewImage podDesignUpscalePage__previewImage"
                                                src={currentImageUrl}
                                                alt="Design preview"
                                            />

                                        </div>

                                    )}

                                    {/* Show the upscaling state */}
                                    {showUpscalingState && (

                                        <div className="podDesignAiPromptForm__staticPreviewStatus">

                                            <div className="ai-in-progress-progress-content">

                                                <div className="ai-image-progress-circle">

                                                    <RadialProgress
                                                        value={getQueueItemProgressValue(upscaleQueueItem)}
                                                        direction={"clockwise"}
                                                        size={200}
                                                        progressColor={getQueueItemProgressColor(upscaleQueueItem)}
                                                        bgColor={"var(--input-border)"}
                                                    />

                                                    <div className="ai-image-progress-text">

                                                        <div>{upscaleQueueItem?.status}</div>
                                                        <div>{upscaleQueueItem?.run_by}</div>

                                                    </div>

                                                </div>

                                            </div>

                                            <h2 className="podDesignAiPage__loadingTitle">
                                                Upscaling Your Image
                                            </h2>

                                            <p className="podDesignAiPage__loadingText">
                                                We’re creating a higher-resolution version of your design image.
                                            </p>

                                        </div>

                                    )}

                                </div>

                                {/* Keep the current dimensions and actions visible */}
                                <div className="podDesignAiPromptForm__staticPreviewActions podDesignUpscalePage__resultBar">

                                    {/* Show the dimensions of the image currently in the preview */}
                                    <div className="podDesignUpscalePage__resultMetadata">

                                        <div className="podDesignUpscalePage__dimensions">

                                            <span>Dimensions</span>

                                            <strong>
                                                {currentWidth.toLocaleString()} × {currentHeight.toLocaleString()} px
                                            </strong>

                                        </div>

                                        {/* Explain when the current image has reached the upscale limit */}
                                        {upscaleLimitReached &&

                                            <span className="podDesignUpscalePage__limitMessage">
                                                Maximum upscale size reached.
                                            </span>

                                        }

                                    </div>

                                    {/* Keep all available actions in a consistent position */}
                                    <div className="podDesignUpscalePage__resultActions">

                                        <CreateButton
                                            title="Reset"
                                            tooltip="Reset to the original image"
                                            size="m"
                                            type="button"
                                            onClick={handleResetUpscale}
                                            disabled={!canReset}
                                        />

                                        <CreateButton
                                            title="Upscale"
                                            tooltip={upscaleLimitReached ? "The current image exceeds the upscale size limit" : "Upscale the current preview using the selected model"}
                                            size="m"
                                            type="button"
                                            onClick={handleUpscaleImage}
                                            disabled={!canUpscale}
                                        />

                                        <CreateButton
                                            title="Submit"
                                            tooltip="Use the current upscaled image"
                                            size="m"
                                            type="button"
                                            onClick={handleSubmitUpscaledImage}
                                            disabled={!canSubmit}
                                        />

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );

}